import { Box, Card, TextField, Typography } from "@mui/material"
import { Image as ImageIcon } from "lucide-react"
import { InlineAiGenerator } from "@crm-src/components/inline-ai-generator"
import { AiTextImprover } from "@crm-src/components/ai-text-improver"
import {
  BROCHURE_COLORS,
  showcaseImageOptions,
  toAbsoluteUrl,
  toPreviewImageUrl,
  type BrochureSection,
} from "./stock-images"
import { BROCHURE_FONT, ctaButton, escapeHtml } from "./html-helpers"

// Editor UI for the showcase panel: a wide image (chosen from the shared stock
// catalog) above a heading + body + optional CTA. The rendered email HTML is
// produced by `showcaseToHtml`. The image is stacked ABOVE the copy (not a
// side-by-side split) because a full-width banner is the most reliable
// image+text pattern across Outlook/Gmail/Apple Mail.
interface ShowcaseSectionProps {
  section: BrochureSection
  onUpdate: (sectionId: string, partial: any) => void
}

export function ShowcaseSection({ section, onUpdate }: ShowcaseSectionProps) {
  const c = section.content
  return (
    <Card sx={{ borderRadius: 0 }} style={{ marginBottom: 0 }}>
      <Box sx={{ p: 3 }}>
        <Typography component="span" sx={{ fontSize: "1.125rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <ImageIcon size={20} />
          Showcase
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Image</Typography>
            <Box sx={{ mt: 1 }}>
              <Box
                component="img"
                src={toPreviewImageUrl(c.imageUrl) + "?w=600&h=260&fit=crop"}
                alt="Showcase"
                sx={{ width: "100%", height: 150, objectFit: "cover", borderRadius: 1, mb: 1.5, display: "block" }}
              />
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1 }}>
                {showcaseImageOptions.map((img) => (
                  <Box
                    component="img"
                    key={img.id}
                    src={toPreviewImageUrl(img.url) + "?w=150&h=100&fit=crop"}
                    alt={img.name}
                    onClick={() => onUpdate(section.id, { imageUrl: img.url })}
                    sx={{
                      width: "100%", height: 56, objectFit: "cover", borderRadius: 1, cursor: "pointer",
                      border: "2px solid",
                      borderColor: c.imageUrl === img.url ? "primary.main" : "divider",
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography component="label" htmlFor={`sc-heading-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Heading</Typography>
              <AiTextImprover fieldType="short" context="brochure showcase heading" currentText={c.heading} onImprove={(v) => onUpdate(section.id, { heading: v })} />
            </Box>
            <TextField
              fullWidth size="small" id={`sc-heading-${section.id}`}
              value={c.heading || ""}
              onChange={(e) => onUpdate(section.id, { heading: e.target.value })}
              placeholder="e.g., Built for how you work"
            />
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography component="label" htmlFor={`sc-body-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Body</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <InlineAiGenerator
                  fieldType="long"
                  context="brochure showcase body — a short paragraph describing the product/offer in this panel"
                  placeholder="e.g., describe this offering"
                  currentValue={c.body}
                  onGenerate={(v) => onUpdate(section.id, { body: v })}
                />
                <AiTextImprover fieldType="long" context="brochure showcase body" currentText={c.body} onImprove={(v) => onUpdate(section.id, { body: v })} />
              </Box>
            </Box>
            <TextField
              fullWidth multiline minRows={3} size="small" id={`sc-body-${section.id}`}
              value={c.body || ""}
              onChange={(e) => onUpdate(section.id, { body: e.target.value })}
              placeholder="Body copy"
            />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
            <TextField
              label="Button label" size="small" InputLabelProps={{ shrink: true }}
              value={c.ctaLabel || ""}
              onChange={(e) => onUpdate(section.id, { ctaLabel: e.target.value })}
              placeholder="e.g., See how it works"
            />
            <TextField
              label="Button link (URL)" size="small" InputLabelProps={{ shrink: true }}
              value={c.ctaUrl || ""}
              onChange={(e) => onUpdate(section.id, { ctaUrl: e.target.value })}
              placeholder="https://xopnetworks.com"
              helperText="Leave blank to hide the button"
            />
          </Box>
        </Box>
      </Box>
    </Card>
  )
}

// Serialize the showcase to email HTML: a full-width image banner above the
// heading / body / optional CTA (a navy-on-light button here for contrast with
// the surrounding white). The <img> carries HTML width/height attributes so
// Outlook renders it at the intended size.
export function showcaseToHtml(section: BrochureSection): string {
  const c = section.content || {}
  const imageUrl = c.imageUrl ? toAbsoluteUrl(c.imageUrl) : ""
  const heading = c.heading ? escapeHtml(c.heading) : ""
  const body = c.body ? escapeHtml(c.body) : ""
  const button = ctaButton(c.ctaUrl || "", c.ctaLabel || "", {
    fill: BROCHURE_COLORS.navy,
    textColor: BROCHURE_COLORS.white,
  })

  if (!imageUrl && !heading && !body && !button) return ""

  return `
          <!-- Showcase -->
          <tr>
            <td bgcolor="${BROCHURE_COLORS.card}" style="background-color: ${BROCHURE_COLORS.card}; padding: 8px 0 0 0;">
              ${imageUrl ? `
              <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
                <tr>
                  <td style="padding: 0; font-size: 0; line-height: 0;">
                    <img src="${imageUrl}" alt="${heading || 'Showcase'}" width="640" style="width: 100%; max-width: 640px; height: auto; display: block; border: 0;" />
                  </td>
                </tr>
              </table>
              ` : ''}
              <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
                <tr>
                  <td class="mobile-pad" style="padding: 32px 40px 40px 40px;">
                    ${heading ? `<h2 style="margin: 0 0 12px 0; color: ${BROCHURE_COLORS.navy}; font-family: ${BROCHURE_FONT}; font-size: 24px; line-height: 30px; font-weight: bold;">${heading}</h2>` : ''}
                    ${body ? `<p style="margin: 0 0 ${button ? '24px' : '0'} 0; color: ${BROCHURE_COLORS.ink}; font-family: ${BROCHURE_FONT}; font-size: 16px; line-height: 25px;">${body}</p>` : ''}
                    ${button ? `<table cellpadding="0" cellspacing="0" border="0" role="presentation"><tr><td>${button}</td></tr></table>` : ''}
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          `
}
